import { ApolloClient, gql, HttpLink, InMemoryCache } from "@apollo/client/core";
import {PreferenceResponse, PreferenceRecordInput, PreferenceRecord} from "./interfaces";
import { GqlClientV2 } from "../client/gqlV2/GqlV2Client";

/**
 * GraphQL client specifically designed for managing Argo user preferences
 * Provides methods to retrieve and save user preference data through GraphQL operations
 */
export class ArgoPreferenceClient {
    private client: GqlClientV2;
    private fetch:  ((input: RequestInfo, init?: (RequestInit | undefined)) => Promise<Response>) | undefined;

    /**
     * Creates a new ArgoPreferenceGqlClient instance
     * @param fetch Custom fetch function for HTTP requests
     */
    constructor(
        fetch: ((input: RequestInfo, init?: (RequestInit | undefined)) => Promise<Response>) | undefined
    ) {
        this.fetch = fetch;
        this.client = new GqlClientV2(this.fetch);
    }

    /**
     * Retrieves multiple user preferences in a single batch operation
     * Uses GraphQL query to fetch preference data based on application name and key pairs
     * @param inputList Array of preference record inputs specifying which preferences to retrieve
     * @returns Promise resolving to PreferenceResponse containing the requested preference data
     */
    batchGetUserPreference =  async (inputList: [PreferenceRecordInput]): Promise <PreferenceResponse> => {
        return await this.client.getClient().query({
            query: gql`query BatchGetUserPreference($getUserPreferenceList: [GetUserPreferenceItemInput!]!) {
                batchGetUserPreference(getUserPreferenceList: $getUserPreferenceList) {
                    preferenceList {
                        applicationName
                        key
                        preferenceData
                    }
                }
            }
`,
            variables: {
                getUserPreferenceList: inputList
            }
        }).then((response) => {
            return response.data.batchGetUserPreference as PreferenceResponse;
        })
    }

    /**
     * Saves multiple user preferences in a single batch operation
     * Uses GraphQL mutation to persist preference data to the backend
     * @param preferenceList Array of preference records to save, including application name, key, and data
     * @returns Promise resolving to PreferenceResponse containing the saved preference data
     */
    batchSaveUserPreference = async (preferenceList: PreferenceRecord[]): Promise<PreferenceResponse> => {
        return await this.client.getClient().mutate({
            mutation: gql`
                mutation BatchSaveUserPreference($preferenceList: [PreferenceInput!]!) {
                    batchSaveUserPreference(preferenceList: $preferenceList) {
                        preferenceList {
                            applicationName
                            key
                            preferenceData
                        }
                    }
                }
            `,
            variables: {
                preferenceList
            }
        }).then((response) => {
            return response.data.batchSaveUserPreference as PreferenceResponse;
        });
    }
}